// Spotify OAuth PKCE Authentication with URL-based Token Sharing
// Tokens are encoded in the URL hash for sharing via QR code or link

import LZString from 'lz-string';

const CLIENT_ID = '6122852fc6204d96aa75183f89110d90';
const REDIRECT_URI = window.location.hostname === 'localhost'
    ? 'http://localhost:3000/q'
    : 'https://divy.live/q';
const SCOPES = [
    'user-modify-playback-state',
    'user-read-playback-state',
    'user-read-email',
    'user-read-private',
    'playlist-read-private',
    'playlist-read-collaborative',
].join(' ');

const VERIFIER_KEY = 'spotify_code_verifier';
const LOCAL_SESSION_KEY = 'queue_session';

// Generate a random string for code verifier
function generateRandomString(length) {
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const values = crypto.getRandomValues(new Uint8Array(length));
    return Array.from(values)
        .map((x) => possible[x % possible.length])
        .join('');
}

// Generate SHA256 hash then base64url encode
async function generateCodeChallenge(verifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

// Encode session data for URL sharing (compressed for shorter QR codes)
export function encodeSession(accessToken, refreshToken, tokenExpiresAt, sessionExpiresAt) {
    const data = {
        a: accessToken,
        r: refreshToken,
        te: tokenExpiresAt,
        se: sessionExpiresAt,
    };
    // Use LZ-string compression for URL-safe, shorter encoding
    return LZString.compressToEncodedURIComponent(JSON.stringify(data));
}

// Decode session from URL or input
export function decodeSession(encoded) {
    try {
        // Try compressed format first
        const decompressed = LZString.decompressFromEncodedURIComponent(encoded);
        if (decompressed) {
            const data = JSON.parse(decompressed);
            return {
                accessToken: data.a,
                refreshToken: data.r,
                tokenExpiresAt: data.te,
                sessionExpiresAt: data.se,
            };
        }
        // Fall back to old base64 format for backwards compatibility
        const data = JSON.parse(atob(encoded));
        return {
            accessToken: data.a,
            refreshToken: data.r,
            tokenExpiresAt: data.te,
            sessionExpiresAt: data.se,
        };
    } catch (e) {
        console.error('Failed to decode session:', e);
        return null;
    }
}

// Get session from URL hash or localStorage
export function getSession() {
    // First check URL hash
    const hash = window.location.hash;
    if (hash && hash.startsWith('#s=')) {
        const encoded = hash.slice(3);
        const session = decodeSession(encoded);
        if (session && new Date(session.sessionExpiresAt) > new Date()) {
            // Also save to localStorage for this device
            saveSessionLocally(session);
            return session;
        }
    }

    // Check localStorage
    const stored = localStorage.getItem(LOCAL_SESSION_KEY);
    if (stored) {
        const session = JSON.parse(stored);
        if (new Date(session.sessionExpiresAt) > new Date()) {
            return session;
        }
        // Expired, clear it
        localStorage.removeItem(LOCAL_SESSION_KEY);
    }

    return null;
}

// Save session to localStorage
function saveSessionLocally(session) {
    localStorage.setItem(LOCAL_SESSION_KEY, JSON.stringify(session));
}

// Generate shareable URL with encoded session
export function getShareableUrl(session) {
    const encoded = encodeSession(
        session.accessToken,
        session.refreshToken,
        session.tokenExpiresAt,
        session.sessionExpiresAt
    );
    const baseUrl = window.location.origin + '/q';
    return `${baseUrl}#s=${encoded}`;
}

// Initiate Spotify OAuth login (admin only)
export async function initiateLogin() {
    const verifier = generateRandomString(64);
    const challenge = await generateCodeChallenge(verifier);

    localStorage.setItem(VERIFIER_KEY, verifier);

    const params = new URLSearchParams({
        client_id: CLIENT_ID,
        response_type: 'code',
        redirect_uri: REDIRECT_URI,
        scope: SCOPES,
        code_challenge_method: 'S256',
        code_challenge: challenge,
    });

    window.location.href = `https://accounts.spotify.com/authorize?${params.toString()}`;
}

// Handle OAuth callback
export async function handleCallback(code) {
    const verifier = localStorage.getItem(VERIFIER_KEY);
    if (!verifier) {
        throw new Error('No code verifier found');
    }

    const response = await fetch('https://accounts.spotify.com/api/token', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
            client_id: CLIENT_ID,
            grant_type: 'authorization_code',
            code,
            redirect_uri: REDIRECT_URI,
            code_verifier: verifier,
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error_description || 'Token exchange failed');
    }

    const tokens = await response.json();
    localStorage.removeItem(VERIFIER_KEY);

    // Create session with 30 min expiry
    const session = {
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        tokenExpiresAt: new Date(Date.now() + tokens.expires_in * 1000).toISOString(),
        sessionExpiresAt: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
    };

    // Save locally
    saveSessionLocally(session);

    return session;
}

// Refresh access token
export async function refreshAccessToken(session) {
    if (!session?.refreshToken) {
        throw new Error('No refresh token available');
    }

    const response = await fetch('https://accounts.spotify.com/api/token', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
            client_id: CLIENT_ID,
            grant_type: 'refresh_token',
            refresh_token: session.refreshToken,
        }),
    });

    if (!response.ok) {
        throw new Error('Token refresh failed');
    }

    const tokens = await response.json();

    // Update session
    session.accessToken = tokens.access_token;
    session.tokenExpiresAt = new Date(Date.now() + tokens.expires_in * 1000).toISOString();
    if (tokens.refresh_token) {
        session.refreshToken = tokens.refresh_token;
    }

    saveSessionLocally(session);
    return session.accessToken;
}

// Get valid access token
export async function getAccessToken() {
    const session = getSession();
    if (!session) {
        return null;
    }

    // Check if token is about to expire (within 5 minutes)
    if (new Date(session.tokenExpiresAt) <= new Date(Date.now() + 5 * 60 * 1000)) {
        try {
            return await refreshAccessToken(session);
        } catch (error) {
            console.error('Failed to refresh token:', error);
            return null;
        }
    }

    return session.accessToken;
}

// Check if session is unlocked
export function isSessionUnlocked() {
    return getSession() !== null;
}

// Get session expiry time
export function getSessionExpiry() {
    const session = getSession();
    if (!session) return null;
    return new Date(session.sessionExpiresAt).getTime();
}

// Clear session
export function clearSession() {
    localStorage.removeItem(LOCAL_SESSION_KEY);
    // Clear URL hash
    if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname);
    }
}

// Extend session by 30 minutes (admin only, max 1 hour total)
export function extendSession() {
    const session = getSession();
    if (!session) return null;

    const MAX_SESSION_MS = 60 * 60 * 1000; // 1 hour max
    const currentRemaining = new Date(session.sessionExpiresAt).getTime() - Date.now();
    const newRemaining = currentRemaining + 30 * 60 * 1000;

    // Cap at 1 hour
    const cappedRemaining = Math.min(newRemaining, MAX_SESSION_MS);
    const newExpiry = new Date(Date.now() + cappedRemaining).toISOString();

    session.sessionExpiresAt = newExpiry;
    saveSessionLocally(session);

    // Update URL hash with new session
    const url = getShareableUrl(session);
    window.history.replaceState(null, '', url);

    return new Date(newExpiry).getTime();
}

// Check if user is admin (Divyesh)
export function isAdminUser(user) {
    if (!user) return false;

    const email = user.email?.toLowerCase() || '';
    const displayName = user.display_name?.toLowerCase() || '';

    return (
        email === 'divyesh.thirukonda@gmail.com' ||
        displayName.includes('divyesh')
    );
}

// End session early (sets timer to 5 seconds)
export function endSession() {
    const session = getSession();
    if (!session) return null;

    // Set session to expire in 5 seconds
    const newExpiry = new Date(Date.now() + 5 * 1000).toISOString();
    session.sessionExpiresAt = newExpiry;
    saveSessionLocally(session);

    // Update URL hash with new session
    const url = getShareableUrl(session);
    window.history.replaceState(null, '', url);

    return new Date(newExpiry).getTime();
}

